import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import { type TypeSharedModel } from '../../../common/dataTypes';
import { createTanstackTable } from '../../../common/utils';
import { useGetProjectById } from '../../../hooks';
import { useGetReceivedModels } from '../../../hooks/tanstack/useReceivedModelsHooks';
import {
  useGetRouteParams,
  useGetSearchResults,
} from '../../../hooks/tanstack/useUtilsHook';
import SearchInput from '../../SearchInput';
import TanstackTable from '../../TanstackTable';

export type DataRow = {
  id: number;
  name: string;
  description: string;
  sender: {
    email: string;
    full_name: string;
  };
  receiver: {
    email: string;
    full_name: string;
  };
  organization: string;
  createdAt: string;
};

const columnHelper = createColumnHelper<DataRow>();

const columns = ({
  handleSelect,
  isGuest,
}: {
  handleSelect: (id: number) => void;
  isGuest: boolean;
}) => [
  columnHelper.accessor('name', {
    cell: (info) => {
      const id = info.row.original.id;

      return (
        <div
          onClick={() => handleSelect(id)}
          className="group flex items-center gap-2 text-start"
        >
          <p className="w-full">{info.getValue()}</p>
        </div>
      );
    },
    header: () => (
      <div className="flex items-start gap-2">
        <p>Model</p>
      </div>
    ),
  }),
  columnHelper.accessor('description', {
    cell: (info) => {
      const id = info.row.original.id;

      return (
        <div
          onClick={() => handleSelect(id)}
          className="group flex items-center gap-2 text-start"
        >
          <p className="w-full line-clamp-2">{info.getValue()}</p>
        </div>
      );
    },
    header: () => (
      <div className="flex items-start gap-2">
        <p>Description</p>
      </div>
    ),
  }),
  columnHelper.accessor('sender', {
    id: 'sender',
    cell: (info) => {
      const id = info.row.original.id;

      const handleOnclick = () => {
        handleSelect(id);
      };

      return info.getValue() ? (
        <div onClick={handleOnclick} className="w-full">
          {info.getValue().full_name}
        </div>
      ) : (
        <div
          onClick={handleOnclick}
          className="text-gray-400 w-full text-start"
        >
          --
        </div>
      );
    },
    header: () => <div>Shared By</div>,
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    cell: (info) => {
      const id = info.row.original.id;

      const value = info.getValue();

      const handleOnclick = () => {
        handleSelect(id);
      };

      return value ? (
        <div onClick={handleOnclick} className="w-full">
          {format(value as string, 'y/MM/dd hh:mm a')}
        </div>
      ) : (
        <div
          onClick={handleOnclick}
          className="text-gray-400 w-full text-center"
        >
          --
        </div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.createdAt as string).getTime() -
      new Date(b.original.createdAt as string).getTime(),
    header: () => <div>Date received</div>,
  }),
];

const ReceivedModelsTable = () => {
  const [data, setData] = useState<DataRow[]>([]);

  const { projectId, orgId } = useGetRouteParams();

  const form = useForm();

  const { watch } = form;

  const navigate = useNavigate();

  const {
    data: receivedModels,
    isLoading: isLoadingModels,
    isRefetching: isRefetchingModels,
  } = useGetReceivedModels(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const searchText = watch('search');

  const searchResults = useGetSearchResults<TypeSharedModel>({
    searchValue: searchText,
    data: receivedModels,
  });

  useEffect(() => {
    if (receivedModels) {
      setData(
        (searchResults ?? receivedModels).map((r) => ({
          id: r.id,
          name: r.name,
          description: r.description,
          sender: {
            email: r.shared_by?.email,
            full_name: r.shared_by?.full_name,
          },
          createdAt: r.created_at,
        })) as DataRow[]
      );
    }
  }, [receivedModels, searchResults]);

  const handleSelect = (id: number) => {
    navigate(`${id}?type=received`);
  };

  const table = createTanstackTable<DataRow>({
    data,
    columns: columns({
      handleSelect,
      isGuest: !!project?.is_guest_project,
    }),
    defaultSorting: {
      id: 'createdAt',
      desc: true,
    },
  });

  const isLoading = isLoadingModels || isRefetchingModels;

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold">Received</h1>
      <FormProvider {...form}>
        <form className="flex justify-between">
          <div className="flex w-[300px] gap-2">
            <SearchInput />
          </div>
        </form>
      </FormProvider>
      <TanstackTable
        table={table}
        isLoading={isLoading}
        emptyState={searchText ? 'No results' : 'No received models available'}
      />
    </div>
  );
};

export default ReceivedModelsTable;
